import {state} from "./state";

export function initDragListeners() {
    state.map.on("mousemove", (e) => {
        if (!state.dragging) return;

        const {marker, handle, polyline, point, index} = state.dragging;
        const newLatLng = e.latlng;

        if (marker) {
            marker.setLatLng(newLatLng);

            // Update associated polylines
            if (point.polylineLatLngs) {
                // The first point of the polyline corresponds to the marker
                point.polylineLatLngs[0] = newLatLng;

                // If the marker has a parent, update it as well
                const parentId = point.parent;
                if (parentId && state.markers[parentId]) {
                    const parentMarker = state.markers[parentId];
                    const polylineLatLngs = point.polylineLatLngs;
                    const polyline = findPolyline(point); // function to retrieve the polyline
                    if (polyline) polyline.setLatLngs(polylineLatLngs);
                }
            }
        }

        if (handle) {
            const latlngs = polyline.getLatLngs();
            latlngs[index] = newLatLng;
            polyline.setLatLngs(latlngs);
            point.polylineLatLngs = latlngs;
            handle.setLatLng(newLatLng);
        }
    });

    state.map.on("mouseup", () => {
        if (state.dragging) state.map.dragging.enable();
        state.dragging = null;
    });
}

// -----------------------------
// Helper to retrieve the polyline
// -----------------------------
function findPolyline(point) {
    let result = null;
    state.pathsGroup.eachLayer(layer => {
        if (layer.getLatLngs && layer.getLatLngs() === point.polylineLatLngs) {
            result = layer;
        }
    });
    return result;
}

function smoothPath(latlngs, iterations = 2) {
    let pts = latlngs.map(p => L.latLng(p.lat, p.lng));

    for (let k = 0; k < iterations; k++) {
        const newPts = [pts[0]];

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i];
            const p1 = pts[i + 1];

            const q = L.latLng(
                0.75 * p0.lat + 0.25 * p1.lat,
                0.75 * p0.lng + 0.25 * p1.lng
            );
            const r = L.latLng(
                0.25 * p0.lat + 0.75 * p1.lat,
                0.25 * p0.lng + 0.75 * p1.lng
            );

            newPts.push(q, r);
        }

        newPts.push(pts[pts.length - 1]);
        pts = newPts;
    }

    return pts;
}

/* ----------------------------------------------------
 * DRAW PATHS
 * ---------------------------------------------------- */
export function drawPaths() {
    state.pathsGroup.clearLayers();

    Object.values(state.markers).forEach(marker => {
        const parentId = marker.options.point.parent;
        if (!parentId) return;

        const parentMarker = state.markers[parentId];
        if (!parentMarker) return;

        const point = marker.options.point;
        const parentPoint = parentMarker.options.point;

        let className = "map-path";
        if (state.editMode) className += " map-path-edit";
        else if (point.flagged && parentPoint.flagged) className += " map-path-completed";
        else if (!point.locked && parentPoint.flagged) className += " map-path-available";
        else className += " map-path-locked";

        if (!point.polylineLatLngs) {
            const start = marker.getLatLng();
            const end = parentMarker.getLatLng();

            const latlngs = [start];
            point.handlers.forEach((e) => {
                latlngs.push(L.latLng(e.lat, e.lng));
            });
            latlngs.push(end);
            point.polylineLatLngs = latlngs;
        } else {
            const end = parentMarker.getLatLng();
            const latlngs = point.polylineLatLngs;
            latlngs[latlngs.length - 1] = end;
        }

        const displayLatLngs = state.editMode
            ? point.polylineLatLngs
            : smoothPath(point.polylineLatLngs, 3);

        const polyline = L.polyline(displayLatLngs, {
            className,
            interactive: true,
            weight: state.editMode ? 5 : 3,
            smoothFactor: 1.5,
            lineCap: "round",
            lineJoin: "round"
        }).addTo(state.pathsGroup);

        if (state.editMode) {
            createHandles(polyline, point);
            enablePolylineClick(polyline, point);
        }
    });
}

/* ----------------------------------------------------
 * HANDLES
 * ---------------------------------------------------- */
export function createHandles(polyline, point) {
    polyline.handles = [];

    polyline.getLatLngs().slice(1, -1).forEach((latlng, i) => {
        const handle = L.marker(latlng, {
            icon: handleIcon(),
            draggable: true,
            interactive: true
        }).addTo(state.pathsGroup);

        handle.on("drag", (e) => {
            const latlngs = polyline.getLatLngs();
            latlngs[i + 1] = e.target.getLatLng();
            polyline.setLatLngs(latlngs);
            point.polylineLatLngs = latlngs;
        });

        // 🔒 Prevent moving the map while dragging
        handle.on("dragstart", () => {
            state.map.dragging.disable();
        });

        handle.on("dragend", () => {
            state.map.dragging.enable();
            notifyPathHandlesUpdated(polyline.handles, point);
        });

        // ❌ Right-click removal
        handle.on("contextmenu", (e) => {
            e.originalEvent.preventDefault();

            const latlngs = polyline.getLatLngs();
            latlngs.splice(i + 1, 1);
            polyline.setLatLngs(latlngs);
            point.polylineLatLngs = latlngs;

            polyline.handles.forEach(h => state.pathsGroup.removeLayer(h));
            createHandles(polyline, point);
            notifyPathHandlesUpdated(polyline.handles, point);
        });

        polyline.handles.push(handle);
    });
}

function notifyPathHandlesUpdated(handles, point) {
    htmx.trigger('#map-adventure', 'map:update-handlers', {
        point: point,
        handlers: handles.map((h) => {
            const latLng = h.getLatLng();
            return {lat: latLng.lat, lng: latLng.lng}
        })
    });
}

/* ----------------------------------------------------
 * POLYLINE CLICK (ADD / SELECT HANDLE)
 * ---------------------------------------------------- */
function enablePolylineClick(polyline, point) {
    polyline.on("click", (e) => {
        if (!state.editMode) return;

        const map = state.map;
        const latlngs = polyline.getLatLngs();

        let closest = null;
        let minDist = Infinity;

        polyline.handles.forEach((handle, i) => {
            const d = map.latLngToLayerPoint(e.latlng)
                .distanceTo(map.latLngToLayerPoint(handle.getLatLng()));
            if (d < minDist) {
                minDist = d;
                closest = i;
            }
        });

        // Select an existing handle
        if (closest !== null && minDist < 15) {
            const handle = polyline.handles[closest];
            state.map.dragging.disable();
            state.dragging = {
                polyline,
                index: closest + 1,
                handle,
                point
            };
            return;
        }

        // Add a new handle
        let insertIndex = 1;
        let minLineDist = Infinity;

        for (let i = 0; i < latlngs.length - 1; i++) {
            const p1 = map.latLngToLayerPoint(latlngs[i]);
            const p2 = map.latLngToLayerPoint(latlngs[i + 1]);
            const click = map.latLngToLayerPoint(e.latlng);

            const dist = L.LineUtil.pointToSegmentDistance(click, p1, p2);
            if (dist < minLineDist) {
                minLineDist = dist;
                insertIndex = i + 1;
            }
        }

        latlngs.splice(insertIndex, 0, e.latlng);
        polyline.setLatLngs(latlngs);
        point.polylineLatLngs = latlngs;

        polyline.handles.forEach(h => state.pathsGroup.removeLayer(h));
        createHandles(polyline, point);
    });
}

/* ----------------------------------------------------
 * HANDLE ICON
 * ---------------------------------------------------- */
function handleIcon() {
    return L.divIcon({
        className: "",
        html: `
            <div class="
                w-3 h-3
                bg-red-500
                rounded-full
                shadow-lg
                ring-2 ring-red-300/60
                hover:scale-125
                transition
                cursor-grab
            "></div>
        `,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
    });
}